import { SECURITY_CONFIG } from "./securityConfig.js";
import { PERMISSIONS } from "./permissions.js";
/**
 * Token management utilities
 */
export const tokenUtils = {
  // Store access token
  setAccessToken: (token) => {
    localStorage.setItem(SECURITY_CONFIG.TOKEN.ACCESS_TOKEN_KEY, token);
  },

  // Get access token
  getAccessToken: () => {
    return localStorage.getItem(SECURITY_CONFIG.TOKEN.ACCESS_TOKEN_KEY);
  },

  // Store refresh token
  setRefreshToken: (token, rememberMe) => {
    if (!rememberMe) {
      //if remember me is false, store in sessionstorage that is flushed on tab close
      sessionStorage.setItem(SECURITY_CONFIG.TOKEN.REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.setItem(SECURITY_CONFIG.TOKEN.REFRESH_TOKEN_KEY, token);
    }
  },

  // Get refresh token
  getRefreshToken: () => {
    if (sessionStorage.getItem(SECURITY_CONFIG.TOKEN.REFRESH_TOKEN_KEY)) {
      return sessionStorage.getItem(SECURITY_CONFIG.TOKEN.REFRESH_TOKEN_KEY);
    }
    return localStorage.getItem(SECURITY_CONFIG.TOKEN.REFRESH_TOKEN_KEY);
  },

  // Clear all tokens
  clearTokens: () => {
    sessionStorage.removeItem(SECURITY_CONFIG.TOKEN.REFRESH_TOKEN_KEY);
    localStorage.removeItem(SECURITY_CONFIG.TOKEN.REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(SECURITY_CONFIG.TOKEN.ACCESS_TOKEN_KEY);
    localStorage.removeItem(SECURITY_CONFIG.TOKEN.ACCESS_TOKEN_KEY);
  },

  // Check if token is expired
  isTokenExpired: (token) => {
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error("Error parsing token:", error);
      return true;
    }
  },

  // Get token expiry date
  getTokenExpiry: (token) => {
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return new Date(payload.exp * 1000);
    } catch (error) {
      console.error("Error parsing token expiry:", error);
      return null;
    }
  },
  getCsrfToken: () => {
    const regex = /(^|;)\s*csrftoken=([^;]*)/;
    const match = regex.exec(document.cookie);
    return match ? decodeURIComponent(match[2]) : null;
  },
};

/**
 * Role and permission utilities with object-level access
 * Assumes user has EXACTLY ONE role at a time.
 */
export const roleUtils = {
  /**
   * Role-level helpers
   */
  hasRole: (userRole, requiredRole) => {
    if (!userRole || !requiredRole) return false;
    return userRole === requiredRole;
  },

  hasAnyRole: (userRole, requiredRoles) => {
    if (!userRole || !Array.isArray(requiredRoles)) return false;
    return requiredRoles.includes(userRole);
  },
  /**
   * Permission-level helpers (using backend permissions)
   */
  hasPermission: (permissions, permissionKey) => {
    if (!permissions || !permissions.permission_checks) return false;
    return permissions.permission_checks[permissionKey] === true;
  },

  hasAnyPermission: (permissions, permissionKeys) => {
    if (!permissions || !permissions.permission_checks) return false;
    return permissionKeys.some(
      (key) => permissions.permission_checks[key] === true
    );
  },
  /**
   * Role identity checks (useful shortcuts)
   */
  isAdmin: (userRole) =>
    [SECURITY_CONFIG.ROLES.ADMIN, SECURITY_CONFIG.ROLES.SUPER_ADMIN].includes(
      userRole
    ),

  isSuperAdmin: (userRole) => userRole === SECURITY_CONFIG.ROLES.SUPER_ADMIN,

  /**
   * Object-level access utilities
   * These delegate to permission checks + ownership logic
   */

  // ----------------------
  // Projects
  // ----------------------
  canAccessProject: (userRole, userId, project) => {
    if (!project || !userId) return false;
    if (roleUtils.isAdmin(userRole)) return true;

    if (roleUtils.hasPermission(userRole, PERMISSIONS.VIEW_ALL_PROJECTS))
      return true;

    if (
      roleUtils.hasPermission(userRole, PERMISSIONS.VIEW_ASSIGNED_PROJECTS) &&
      (project.project_owner === userId ||
        (Array.isArray(project.project_staff) &&
          project.project_staff.includes(userId)))
    )
      return true;

    return false;
  },

  canEditProject: (userRole, userId, project) => {
    if (!project || !userId) return false;
    if (roleUtils.isAdmin(userRole)) return true;

    if (
      roleUtils.hasPermission(userRole, PERMISSIONS.EDIT_OWN_PROJECT) &&
      project.project_owner === userId
    )
      return true;

    if (
      roleUtils.hasPermission(userRole, PERMISSIONS.EDIT_ASSIGNED_PROJECT) &&
      Array.isArray(project.project_staff) &&
      project.project_staff.includes(userId)
    )
      return true;

    return false;
  },

  canChangeProjectStatus: (userRole, userId, project) => {
    if (!project || !userId) return false;
    if (roleUtils.isAdmin(userRole)) return true;

    return (
      roleUtils.hasPermission(
        userRole,
        PERMISSIONS.CHANGE_OWN_PROJECT_STATUS
      ) && project.project_owner === userId
    );
  },

  canManageProjectMembers: (userRole, userId, project) => {
    return roleUtils.canChangeProjectStatus(userRole, userId, project);
  },
};

/**
 * Session management utilities
 */
export const sessionUtils = {
  // Clear session data
  clearSession: () => {
    tokenUtils.clearTokens();
  },
};

/**
 * Input sanitization utilities
 */
export const sanitizeUtils = {
  // Basic HTML sanitization
  sanitizeHtml: (input) => {
    if (typeof input !== "string") return input;

    return input
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#x27;")
      .replace(/\//g, "&#x2F;");
  },

  // Sanitize for SQL injection prevention (basic)
  sanitizeSql: (input) => {
    if (typeof input !== "string") return input;

    return input
      .replace(/'/g, "''")
      .replace(/;/g, "")
      .replace(/--/g, "")
      .replace(/\/\*/g, "")
      .replace(/\*\//g, "");
  },

  // Remove script tags and dangerous attributes
  sanitizeScript: (input) => {
    if (typeof input !== "string") return input;

    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/javascript:/gi, "")
      .replace(/on\w+="[^"]*"/gi, "")
      .replace(/on\w+='[^']*'/gi, "");
  },
};

export default {
  tokenUtils,
  roleUtils,
  sessionUtils,
  sanitizeUtils,
};
